import { useEffect, useState } from 'react';
import '@/assets/styles/components/meet-completed.styles.scss';
import * as Icons from '@/assets/svg';
import { Button } from '@/components';
import { MeetCompletedProps } from '@/components/meet-completed/meet-completed.types.ts';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { InteractionConnectionStatusOptionResponse } from '@dopingtech/live_expert_bff_api_ts';
import {
	CoachExpertInteractionFeedbackCreateRequest,
	GotItExpertInteractionFeedbackCreateRequest,
	CoachExpertInteractionFeedbackCreateRequestConnectionStatusEnum
} from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { APP_TYPES } from '@/constants';

const optionIcons = {
	'1': (
		<Icons.WifiConnection
			height={80}
			width={80}
		/>
	),
	'2': (
		<Icons.WifiConnectionError
			height={80}
			width={80}
		/>
	),
	'3': (
		<Icons.WifiNetworkNoConnection
			height={80}
			width={80}
		/>
	)
};

const optionStatus = {
	1: CoachExpertInteractionFeedbackCreateRequestConnectionStatusEnum.Completed,
	2: CoachExpertInteractionFeedbackCreateRequestConnectionStatusEnum.CompletedWithMinorConnectionIssues,
	3: CoachExpertInteractionFeedbackCreateRequestConnectionStatusEnum.Failed
};

const MeetCompleted = ({ interactionId, studentId, type = APP_TYPES.COACH }: MeetCompletedProps) => {
	const navigate = useNavigate();
	const location = useLocation();

	const [selectedValue, setSelectedValue] = useState<number | undefined | null>(null);
	const [failId] = useState<number>(3);

	const { FeedbackControllerApi } = useApi();

	const { data: options } = useQueryProvider([QUERY_KEYS.getAllInteractionConnectionStatusOptions], () => FeedbackControllerApi.getAllInteractionConnectionStatusOptions(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: true
	});

	useEffect(() => {
		const hasData = studentId || interactionId;

		if (location?.pathname === NavigationPath.coachMeetCompleted && !hasData) {
			navigate(NavigationPath.coachPastMeetings);
			return;
		}

		if (location?.pathname === NavigationPath.gotItMeetCompleted && !hasData) {
			navigate(NavigationPath.gotItPastMeetings);
			return;
		}
	}, [APP_TYPES, studentId, interactionId]);

	const saveConnectionStatusKY = useMutationProvider((body: CoachExpertInteractionFeedbackCreateRequest) => FeedbackControllerApi.createCoachExpertInteractionFeedback(body), {
		onSuccess: () => {
			toast.success('Bağlantı durumu başarıyla kaydedildi');
		}
	});

	const saveConnectionStatusSA = useMutationProvider((body: GotItExpertInteractionFeedbackCreateRequest) => FeedbackControllerApi.createGotItExpertInteractionFeedback(body), {
		onSuccess: () => {
			toast.success('Bağlantı durumu başarıyla kaydedildi');
		}
	});

	const handleSelect = (option: InteractionConnectionStatusOptionResponse) => {
		setSelectedValue(option?.id);
	};

	const handleClose = async () => {
		if (selectedValue) {
			const body: CoachExpertInteractionFeedbackCreateRequest | GotItExpertInteractionFeedbackCreateRequest = {
				interactionId: interactionId,
				connectionStatus: optionStatus[selectedValue as keyof typeof optionStatus]
			};

			if (type === APP_TYPES.GOT_IT) {
				await saveConnectionStatusSA.mutateAsync(body);

				navigate(NavigationPath.gotItPastMeetingsDetail, {
					state: {
						interactionId: interactionId,
						studentId: studentId
					}
				});
			} else {
				await saveConnectionStatusKY.mutateAsync(body);

				navigate(NavigationPath.coachPastMeetingsDetail, {
					state: {
						interactionId: interactionId,
						studentId: studentId
					}
				});
			}
		}
	};

	return (
		<section className={'meet-completed-container'}>
			<div>
				<h1 className={'meet-completed-title'}>Tebrikler! Görüşmen Tamamlandı!</h1>
				<p>
					Görüşme hakkındaki notlarını <b> Geçmiş Görüşmelerim</b> alanından
					<br />
					kontrol etmeyi unutma 👋
				</p>
			</div>
			<div className={'meet-completed-options-container'}>
				{options?.map((option, index) => (
					<button
						onClick={() => handleSelect(option)}
						className={`meet-completed-option ${option?.id === selectedValue ? 'selected' : null}`}
						key={index}>
						<div>
							<div className={`meet-completed-option-checkbox ${option?.id === selectedValue ? 'selected' : null}`}>
								<div />
							</div>
						</div>
						<div className={'meet-completed-option-info'}>
							{optionIcons[option?.id?.toString() as keyof typeof optionIcons]}
							<p className={'option-item-text'}>{option?.description}</p>
						</div>
					</button>
				))}
			</div>

			{selectedValue && (
				<div className={'meet-completed-actions'}>
					{selectedValue === failId ? (
						<Button
							variant={'success'}
							onClick={() => handleClose()}>
							Kapat
						</Button>
					) : (
						<Button
							variant={'success'}
							onClick={() => handleClose()}>
							Görüşme Detaylarını Gir
						</Button>
					)}
				</div>
			)}
		</section>
	);
};

export default MeetCompleted;
